// @ts-nocheck
export const STATUS = {
  GOOD: "good",
  WARN: "warn",
  BAD: "bad",
  INFO: "info",
  UNKNOWN: "unknown",
};

export const STATUS_COLORS = {
  good: "#10b981",
  warn: "#f59e0b",
  bad: "#ef4444",
  info: "#6b7280",
  unknown: "#9ca3af",
};

export const STATUS_LABELS = {
  good: "Good",
  warn: "Needs attention",
  bad: "Problem",
  info: "Info",
  unknown: "No data",
};

export const METRIC_GUIDE = {
  mos_avg: {
    label: "MOS (average)",
    unit: "",
    digits: 2,
    direction: "higher",
    good: 3.8,
    warn: 3.1,
    target: "4.0 – 4.4 is toll quality",
    means:
      "Mean Opinion Score, 1–5. A single number for how the call sounded, computed from loss, jitter and latency using the ITU-T G.107 E-model.",
    whenBad:
      "Below 3.1 callers notice degradation; below 2.4 the conversation is hard work. Look at loss and latency next to find the cause.",
  },
  mos_min: {
    label: "MOS (worst moment)",
    unit: "",
    digits: 2,
    direction: "higher",
    good: 3.5,
    warn: 2.8,
    target: "Stay above 3.5",
    means:
      "The worst 2-second window across the range. Catches short dropouts that a healthy average hides.",
    whenBad:
      "A low worst-moment with a healthy average means brief but sharp glitches — usually a WiFi hiccup or a burst of loss.",
  },
  inbound_loss_avg: {
    label: "Packet loss — incoming",
    unit: "%",
    digits: 2,
    direction: "lower",
    good: 1,
    warn: 3,
    target: "Under 1%",
    means:
      "Share of audio packets that never reached the agent. This is what the AGENT hears.",
    whenBad:
      "Above 3% produces audible clipping. Cause is usually the agent's own network (WiFi, ISP) or the leg between the SBC and the browser.",
  },
  outbound_loss_avg: {
    label: "Packet loss — outgoing",
    unit: "%",
    digits: 2,
    direction: "lower",
    good: 1,
    warn: 3,
    target: "Under 1%",
    means:
      "Loss reported back by the far end via RTCP — the only measure of what the CUSTOMER hears.",
    whenBad:
      "Above 3% the customer hears the agent breaking up even though the agent hears them fine. Check the agent's upload.",
  },
  jitter_avg_ms: {
    label: "Jitter",
    unit: " ms",
    digits: 1,
    direction: "lower",
    good: 20,
    warn: 40,
    target: "Under 20 ms",
    means:
      "Variation in packet arrival timing. The jitter buffer absorbs small amounts; beyond that it causes gaps.",
    whenBad:
      "Above 40 ms means an unstable path — shared WiFi, a congested link, or a VPN. Wired ethernet usually fixes it.",
  },
  rtt_avg_ms: {
    label: "Round-trip latency",
    unit: " ms",
    digits: 1,
    direction: "lower",
    good: 150,
    warn: 250,
    target: "Under 150 ms",
    means: "Time for audio to travel to the far end and back.",
    whenBad:
      "Past 250 ms people start talking over each other; past 500 ms the call feels like satellite. Often a geographically distant media path or a TURN relay.",
  },
  concealment_avg_percent: {
    label: "Audio gaps (concealed)",
    unit: "%",
    digits: 2,
    direction: "lower",
    good: 2,
    warn: 5,
    target: "Under 2%",
    means:
      "Share of audio the decoder had to invent because packets were missing. Silence-only concealment is excluded, so this is what was actually audible.",
    whenBad:
      "Above 5% is choppy, robotic-sounding audio. Always accompanied by loss or jitter — fix those.",
  },
  send_delay_avg_ms: {
    label: "Uplink send delay",
    unit: " ms",
    digits: 1,
    direction: "lower",
    good: 20,
    warn: 40,
    target: "Under 20 ms",
    means:
      "Average time each packet waited in the agent's send queue. Rises BEFORE loss appears, so it is the earliest warning of a congested upload.",
    whenBad:
      "Above 40 ms something else on the agent's connection is saturating the upload (backups, cloud sync, video streams).",
  },
  echo_return_loss_avg_db: {
    label: "Echo return loss (ERL)",
    unit: " dB",
    digits: 1,
    direction: "higher",
    good: 25,
    warn: 20,
    target: "Above 25 dB — higher is better",
    means:
      "How much speaker sound the echo canceller removes before it re-enters the microphone. HIGHER is better.",
    whenBad:
      "Below 20 dB the customer may hear their own voice back. Almost always caused by using laptop speakers instead of a headset.",
  },
  mic_dropped_seconds: {
    label: "Microphone audio dropped",
    unit: " s",
    digits: 2,
    direction: "lower",
    good: 0.05,
    warn: 0.5,
    target: "Effectively zero",
    means:
      "Seconds of audio the browser lost at the capture stage — before the network is involved at all.",
    whenBad:
      "Any meaningful value points at the agent's machine, not the line: CPU overload, a failing headset, or a bad audio driver.",
  },
  agent_talk_share_percent: {
    label: "Agent talk share",
    unit: "%",
    digits: 1,
    direction: "band",
    bandGood: [35, 65],
    bandWarn: [25, 75],
    target: "40 – 60% is a balanced conversation",
    means:
      "Share of speech energy coming from the agent vs the customer. Derived from audio energy only — no speech recognition, no recording analysis.",
    whenBad:
      "Very high means the agent is monologuing; very low means they are barely engaging. Treat as a coaching signal, not a hard fault.",
  },
  audio_start_latency_ms: {
    label: "Audio start delay",
    unit: " ms",
    digits: 0,
    direction: "lower",
    good: 500,
    warn: 1500,
    target: "Under 500 ms",
    means:
      "Gap between the call connecting and the first audio packet arriving — the 'dead air after answer' the customer experiences.",
    whenBad:
      "Above 1.5s customers say 'hello?' twice or hang up. Usually SBC/media-negotiation delay rather than an agent problem.",
  },
  poor_call_percent: {
    label: "Poor calls",
    unit: "%",
    digits: 1,
    direction: "lower",
    good: 5,
    warn: 15,
    target: "Under 5% of calls",
    means: "Share of this agent's calls whose average MOS fell below 3.1.",
    whenBad:
      "Above 15% is a persistent problem, not bad luck — check whether it is always the same agent (their setup) or all agents (the platform).",
  },
  inbound_bitrate_avg_kbps: {
    label: "Bitrate in",
    unit: " kbps",
    digits: 1,
    direction: "info",
    target: "~64–90 for PCMU, ~30–50 for Opus",
    means: "Audio throughput received. Mostly a codec sanity check.",
    whenBad: "Far below the codec's expected rate suggests packets were being dropped upstream.",
  },
  outbound_bitrate_avg_kbps: {
    label: "Bitrate out",
    unit: " kbps",
    digits: 1,
    direction: "info",
    target: "~64–90 for PCMU, ~30–50 for Opus",
    means: "Audio throughput sent.",
    whenBad: "A collapse here alongside send delay confirms uplink congestion.",
  },
  relay_calls: {
    label: "Calls via TURN relay",
    unit: "",
    digits: 0,
    direction: "info",
    target: "Ideally zero",
    means:
      "Calls whose media had to be relayed instead of going direct. Adds a latency floor.",
    whenBad:
      "A high count means the agent's network blocks direct UDP — a firewall/NAT policy worth raising with IT.",
  },
  narrowband_calls: {
    label: "Narrowband calls",
    unit: "",
    digits: 0,
    direction: "info",
    target: "Depends on your carrier",
    means:
      "Calls negotiated at 8 kHz (PCMU/PCMA) rather than wideband — no HD voice.",
    whenBad:
      "Expected on PSTN routes. Unexpected on internal/SIP-trunk calls means codec negotiation is falling back.",
  },
  path_change_calls: {
    label: "Calls that switched network",
    unit: "",
    digits: 0,
    direction: "info",
    target: "Ideally zero",
    means:
      "Calls where the media path moved mid-conversation (WiFi to LTE, access-point handoff).",
    whenBad:
      "Each switch causes a brief glitch. Recurring counts mean the agent is on unstable WiFi — move them to ethernet.",
  },
  calls: {
    label: "Monitored calls",
    unit: "",
    digits: 0,
    direction: "info",
    target: "—",
    means:
      "WebRTC / Soft Phone calls with quality data in this range. Mobile-profile calls have no browser media session and are never counted.",
    whenBad: "A low count next to a high total call count means the agent mostly used a Mobile profile.",
  },
};

const num = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const x = Number(v);
  return Number.isFinite(x) ? x : null;
};

/**
 * Rate one metric value.
 * @returns {{status: string, color: string, label: string, guide: object|null}}
 */
export const rateMetric = (key, value) => {
  const guide = METRIC_GUIDE[key] || null;
  const v = num(value);

  if (!guide || v === null) {
    return {
      status: v === null ? STATUS.UNKNOWN : STATUS.INFO,
      color: STATUS_COLORS[v === null ? STATUS.UNKNOWN : STATUS.INFO],
      label: STATUS_LABELS[v === null ? STATUS.UNKNOWN : STATUS.INFO],
      guide,
    };
  }

  let status = STATUS.INFO;

  if (guide.direction === "lower") {
    status = v <= guide.good ? STATUS.GOOD : v <= guide.warn ? STATUS.WARN : STATUS.BAD;
  } else if (guide.direction === "higher") {
    status = v >= guide.good ? STATUS.GOOD : v >= guide.warn ? STATUS.WARN : STATUS.BAD;
  } else if (guide.direction === "band") {
    const [gLo, gHi] = guide.bandGood;
    const [wLo, wHi] = guide.bandWarn;
    status =
      v >= gLo && v <= gHi ? STATUS.GOOD : v >= wLo && v <= wHi ? STATUS.WARN : STATUS.BAD;
  }

  return {
    status,
    color: STATUS_COLORS[status],
    label: STATUS_LABELS[status],
    guide,
  };
};

/** Format a value using its metric's unit + precision. */
export const formatMetric = (key, value) => {
  const guide = METRIC_GUIDE[key];
  const v = num(value);
  if (v === null) return "—";
  const digits = guide ? guide.digits : 2;
  const unit = guide ? guide.unit : "";
  return `${Number(v.toFixed(digits))}${unit}`;
};

/**
 * One-line overall verdict for a metrics summary — what a supervisor should read
 * first. Picks the most severe finding rather than averaging everything.
 */
export const buildVerdict = (summary) => {
  if (!summary || !num(summary.calls)) {
    return {
      status: STATUS.UNKNOWN,
      color: STATUS_COLORS.unknown,
      headline: "No WebRTC quality data",
      detail:
        "This agent made no Soft Phone / WebRTC calls in this range, or was using a Mobile profile (which has no browser media session to measure).",
    };
  }

  const calls = num(summary.calls) || 0;
  const poorPct = calls > 0 ? ((num(summary.poor_calls) || 0) / calls) * 100 : null;

  const checks = [
    ["mos_avg", summary.mos_avg],
    ["poor_call_percent", poorPct],
    ["inbound_loss_avg", summary.inbound_loss_avg],
    ["outbound_loss_avg", summary.outbound_loss_avg],
    ["jitter_avg_ms", summary.jitter_avg_ms],
    ["rtt_avg_ms", summary.rtt_avg_ms],
    ["concealment_avg_percent", summary.concealment_avg_percent],
    ["send_delay_avg_ms", summary.send_delay_avg_ms],
    ["echo_return_loss_avg_db", summary.echo_return_loss_avg_db],
    ["mic_dropped_seconds", summary.mic_dropped_seconds],
  ];

  const rated = checks
    .map(([key, value]) => ({ key, value, ...rateMetric(key, value) }))
    .filter((r) => r.status === STATUS.BAD || r.status === STATUS.WARN);

  const bad = rated.filter((r) => r.status === STATUS.BAD);
  const warn = rated.filter((r) => r.status === STATUS.WARN);

  if (bad.length === 0 && warn.length === 0) {
    return {
      status: STATUS.GOOD,
      color: STATUS_COLORS.good,
      headline: `Healthy — MOS ${formatMetric("mos_avg", summary.mos_avg)} across ${calls} call${calls === 1 ? "" : "s"}`,
      detail: "Every measured metric is inside its target range. No action needed.",
    };
  }

  const worst = (bad[0] || warn[0]);
  const others = rated.filter((r) => r.key !== worst.key);
  const status = bad.length ? STATUS.BAD : STATUS.WARN;

  return {
    status,
    color: STATUS_COLORS[status],
    headline: `${worst.guide.label} is ${status === STATUS.BAD ? "a problem" : "borderline"} — ${formatMetric(worst.key, worst.value)}`,
    detail:
      worst.guide.whenBad +
      (others.length
        ? ` Also outside target: ${others.map((r) => `${r.guide.label} (${formatMetric(r.key, r.value)})`).join(", ")}.`
        : ""),
  };
};

/** Human text for warning codes raised during calls. */
export const WARNING_TEXT = {
  inbound_loss: "Packet loss on incoming audio",
  inbound_loss_high: "Heavy packet loss on incoming audio",
  outbound_loss: "Loss on outgoing audio (customer side)",
  outbound_loss_high: "Customer was losing the agent's audio",
  jitter: "Network jitter",
  jitter_high: "Severe network jitter",
  rtt: "High latency",
  rtt_high: "Very high latency",
  audio_gaps: "Some choppy audio",
  audio_gaps_high: "Choppy incoming audio",
  echo: "Weak echo cancellation",
  echo_high: "Echo likely — customer may hear themselves",
  mic_capture_drops: "Microphone dropped audio (headset / PC load)",
  uplink_congested: "Agent upload queueing",
  uplink_congested_high: "Agent upload congested",
  clock_drift: "Audio stretched to track clock drift",
  network_path_changed: "Network path changed mid-call",
  narrowband_codec: "Narrowband codec — no HD voice",
  no_rtcp_reports: "No RTCP from far end — outgoing quality unmeasured",
  mic_silent: "No audio from the agent's microphone",
  remote_silent: "No audio from the other party",
  no_inbound_media: "One-way audio — nothing received",
  no_outbound_media: "One-way audio — nothing sent",
  relay: "Media went through a TURN relay",
};

/** Advice for each warning code — what to actually do about it. */
export const WARNING_ADVICE = {
  inbound_loss: "Check the agent's local network; prefer wired ethernet over WiFi.",
  inbound_loss_high: "Agent's downlink is unreliable — move them off WiFi and re-test.",
  outbound_loss: "Agent's upload is dropping packets; check for other upload traffic.",
  outbound_loss_high: "Customers cannot hear this agent properly — treat as urgent.",
  jitter: "Unstable path. Wired connection or a less congested WiFi channel.",
  jitter_high: "Severe instability — likely shared WiFi or a VPN in the media path.",
  rtt: "Media path may be routing far away; check SBC/TURN region.",
  rtt_high: "Verify the media is not relaying through a distant TURN server.",
  audio_gaps: "Symptom of loss/jitter — fix those first.",
  audio_gaps_high: "Callers hear robotic audio; address the underlying loss.",
  echo: "Ask the agent to use a headset rather than laptop speakers.",
  echo_high: "Headset required — customers are hearing their own voice back.",
  mic_capture_drops: "Agent's PC or headset is at fault, not the network. Check CPU load and USB port.",
  uplink_congested: "Something else is using the agent's upload bandwidth.",
  uplink_congested_high: "Stop background uploads / cloud sync on the agent's machine.",
  clock_drift: "Usually a driver or virtual-audio-device issue on the agent's PC.",
  network_path_changed: "Agent roamed between networks mid-call — put them on a stable connection.",
  narrowband_codec: "Expected on PSTN routes; investigate only for internal calls.",
  no_rtcp_reports: "Outgoing quality is unmeasurable for these calls — check SBC RTCP handling.",
  mic_silent: "Wrong input device selected, or the mic is muted at OS level.",
  remote_silent: "Far end sent no audio — often a carrier or IVR-side issue.",
  no_inbound_media: "One-way audio: check NAT/firewall and SBC media routing.",
  no_outbound_media: "One-way audio: agent's media is not reaching the SBC.",
  relay: "Direct UDP is blocked on the agent's network — raise with their IT.",
};

export default {
  STATUS,
  STATUS_COLORS,
  STATUS_LABELS,
  METRIC_GUIDE,
  rateMetric,
  formatMetric,
  buildVerdict,
  WARNING_TEXT,
  WARNING_ADVICE,
};


export const mosQuality = (mos) => {
  const m = num(mos);
  if (m === null) return { key: "unknown", label: "No data", color: STATUS_COLORS.unknown };
  if (m >= 4.2) return { key: "excellent", label: "Excellent", color: "#10b981" };
  if (m >= 3.8) return { key: "good", label: "Good", color: "#22c55e" };
  if (m >= 3.1) return { key: "fair", label: "Okay", color: "#f59e0b" };
  if (m >= 2.4) return { key: "poor", label: "Poor", color: "#f97316" };
  return { key: "bad", label: "Bad", color: "#ef4444" };
};

const QUALITY_STATUS = {
  excellent: STATUS.GOOD,
  good: STATUS.GOOD,
  fair: STATUS.WARN,
  poor: STATUS.BAD,
  bad: STATUS.BAD,
  unknown: STATUS.UNKNOWN,
};

/** One-line description shown under each simple metric. */
export const SIMPLE_DESC = {
  quality: "Overall how the call sounded, scored out of 5.",
  loss: "Bits of audio lost on the way. Under 1% is normal.",
  jitter: "How evenly the audio arrived. High jitter makes voices choppy.",
  delay: "Time for audio to reach the other side and come back. Under 150 ms feels natural.",
  buffer: "Extra wait added to smooth out uneven audio. Higher means a slower-feeling call.",
  gaps: "Bits of audio the app had to fill in. Heard as robotic or broken speech.",
};

/** Rate a value and keep only what a tile needs. */
const tile = (key, label, value, text, desc) => {
  const { status, color } = rateMetric(key, value);
  return { key: label.toLowerCase(), label, value: text, status, color, desc };
};

export const buildSimpleMetrics = (src) => {
  if (!src) return [];

  const inLoss = num(src.inbound_loss_avg);
  const outLoss = num(src.outbound_loss_avg);
  // Worst of the two directions: the customer notices either one.
  const loss = inLoss === null && outLoss === null ? null : Math.max(inLoss ?? 0, outLoss ?? 0);
  const q = mosQuality(src.mos_avg);

  return [
    {
      key: "quality",
      label: "Call quality",
      value: q.label,
      sub: num(src.mos_avg) === null ? "" : `${formatMetric("mos_avg", src.mos_avg)} out of 5`,
      color: q.color,
      status: QUALITY_STATUS[q.key],
      desc: SIMPLE_DESC.quality,
    },
    tile(
      "inbound_loss_avg",
      "Audio drops",
      loss,
      loss === null ? "—" : `${Number(loss.toFixed(2))}%`,
      SIMPLE_DESC.loss
    ),
    tile(
      "jitter_avg_ms",
      "Jitter",
      src.jitter_avg_ms,
      formatMetric("jitter_avg_ms", src.jitter_avg_ms),
      SIMPLE_DESC.jitter
    ),
    tile(
      "rtt_avg_ms",
      "Delay",
      src.rtt_avg_ms,
      formatMetric("rtt_avg_ms", src.rtt_avg_ms),
      SIMPLE_DESC.delay
    ),
  ];
};

const PROBLEM_TEXT = {
  "audio drops": "audio was dropping out",
  jitter: "audio arrived unevenly (jitter)",
  delay: "there was a noticeable delay",
};

export const buildSimpleVerdict = (summary) => {
  if (!summary || !num(summary.calls)) {
    return {
      status: STATUS.UNKNOWN,
      color: STATUS_COLORS.unknown,
      headline: "No call quality data yet",
      detail: "Quality is only measured on calls made from the browser softphone.",
    };
  }

  const calls = num(summary.calls) || 0;
  const metrics = buildSimpleMetrics(summary).filter((m) => m.key !== "quality");
  const bad = metrics.filter((m) => m.status === STATUS.BAD);
  const warn = metrics.filter((m) => m.status === STATUS.WARN);

  if (!bad.length && !warn.length) {
    return {
      status: STATUS.GOOD,
      color: STATUS_COLORS.good,
      headline: `Calls sounded ${mosQuality(summary.mos_avg).label.toLowerCase()}`,
      detail: `Across ${calls} call${calls === 1 ? "" : "s"}, nothing needed attention.`,
    };
  }

  const worst = bad[0] || warn[0];
  const rest = [...bad, ...warn].filter((m) => m.key !== worst.key);

  return {
    status: bad.length ? STATUS.BAD : STATUS.WARN,
    color: STATUS_COLORS[bad.length ? STATUS.BAD : STATUS.WARN],
    headline: bad.length
      ? `Some calls had problems — ${PROBLEM_TEXT[worst.key]}`
      : `Calls were mostly fine — ${PROBLEM_TEXT[worst.key]} at times`,
    detail:
      `${worst.label}: ${worst.value}. ${worst.desc}` +
      (rest.length
        ? ` Also worth a look: ${rest.map((m) => `${m.label} ${m.value}`).join(", ")}.`
        : ""),
  };
};

